/**
 * Gift card view component.
 * @module components/View/GiftCardView
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Confirm,
  Table,
  TableRow,
  TableHeader,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent, getVocabulary } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';
import downloadSVG from '@plone/volto/icons/download.svg';
import giftcardSvg from './giftcard.svg';

const CARD_WIDTH = 1000;
const CARD_HEIGHT = 600;

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  let hours = parsed.getHours();
  const minutes = parsed.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${year}-${month}-${day} ${hours}:${minutesStr} ${ampm}`;
};

// EUR has no live exchange rate of its own yet, so it's shown as a flat
// $5 markup over the USD price.
const formatPrice = (price, currency) => {
  if (!price) return 'N/A';
  return currency === 'EUR' ? `€ ${Number(price) + 5}` : `$ ${price}`;
};

// The card artwork's amount/code text are sized for roughly a 5- and a
// 19-character string respectively; longer actual values are scaled down
// so they still fit within the card instead of overflowing it.
const fitFontSize = (text, baseSize, baseLength) =>
  text.length > baseLength
    ? Math.max(baseSize * 0.4, (baseSize * baseLength) / text.length)
    : baseSize;

/**
 * Gift card view component class.
 * @function GiftCardView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const GiftCardView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
    dispatch(getVocabulary({ vocabNameOrURL: 'giftcard_status' }));
  }, [dispatch]);

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );
  const statusVocabulary = useSelector(
    (state) => state.vocabularies?.giftcard_status?.items || [],
  );

  const contactTitle =
    contactsVocabulary.find((item) => item.value === content.contact)?.label ||
    content.contact;
  const statusTitle =
    statusVocabulary.find((item) => item.value === content.status)?.label ||
    content.status;
  const statusClass =
    content.status === 'redeemed' ? 'status-paid' : 'status-open';

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  // Renders the same artwork + overlay shown on screen into an offscreen
  // canvas (at 2x resolution for a crisp download) and saves it as a PNG.
  const onDownload = async () => {
    const amountText = formatPrice(content.amount, currency);
    const codeText = content.code || '';
    const amountFontSize = fitFontSize(amountText, 130, 5);
    const codeFontSize = fitFontSize(codeText, 34, 19);

    const svgText = await (await fetch(giftcardSvg)).text();
    const overlayMarkup = `
      <text x="500" y="322" text-anchor="middle" font-family="Montserrat, Helvetica, Arial, sans-serif" font-weight="700" fill="#ffffff" font-size="${amountFontSize}">${escapeXml(amountText)}</text>
      <text x="86" y="502" font-family="'Courier New', Courier, monospace" font-weight="700" fill="#ffffff" letter-spacing="5" font-size="${codeFontSize}">${escapeXml(codeText)}</text>
    `;
    const combinedSvg = svgText.replace('</svg>', `${overlayMarkup}</svg>`);

    const svgUrl = URL.createObjectURL(
      new Blob([combinedSvg], { type: 'image/svg+xml;charset=utf-8' }),
    );

    const image = new Image();
    image.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = CARD_WIDTH * scale;
      canvas.height = CARD_HEIGHT * scale;
      canvas
        .getContext('2d')
        .drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(svgUrl);

      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `giftcard-${content.code || 'card'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
    };
    image.src = svgUrl;
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <button
          type="button"
          className="circular-button primary float-left"
          onClick={onDownload}
          aria-label="Download"
        >
          <Icon name={downloadSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">Gift Card</div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete gift card"
        content={`Are you sure you want to delete "${content.code}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="gift-card">
          <h1 className="content-title">{content.code}</h1>
          <Table celled>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  rowSpan="5"
                  textAlign="center"
                  className="general sub rotated"
                >
                  <span className="rotate-label">General</span>
                </TableHeaderCell>
                <TableHeaderCell width="6">Code</TableHeaderCell>
                <TableCell width="6">{content.code}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableCell>{formatPrice(content.amount, currency)}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Contact</TableHeaderCell>
                <TableCell>{contactTitle}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableCell>{formatDate(content.date)}</TableCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableCell className={statusClass}>{statusTitle}</TableCell>
              </TableRow>
            </TableHeader>
          </Table>
          <div className="giftcard-visual">
            <img src={giftcardSvg} alt="Gift card" />
            <svg className="giftcard-overlay" viewBox="0 0 1000 600">
              <text
                x="500"
                y="322"
                textAnchor="middle"
                fontFamily="Montserrat, Helvetica, Arial, sans-serif"
                fontWeight="700"
                fill="#ffffff"
                fontSize={fitFontSize(
                  formatPrice(content.amount, currency),
                  130,
                  5,
                )}
              >
                {formatPrice(content.amount, currency)}
              </text>
              <text
                x="86"
                y="502"
                fontFamily="'Courier New', Courier, monospace"
                fontWeight="700"
                fill="#ffffff"
                letterSpacing="5"
                fontSize={fitFontSize(content.code || '', 34, 19)}
              >
                {content.code}
              </text>
            </svg>
          </div>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
GiftCardView.propTypes = {
  content: PropTypes.shape({
    code: PropTypes.string,
    amount: PropTypes.number,
    contact: PropTypes.string,
    date: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default GiftCardView;
